import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Container, Typography, Box, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Button,
  Select, MenuItem, FormControl, InputLabel, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Alert
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const API = process.env.REACT_APP_API_URL;

const TIPOS = [
  { value: 'billable', label: 'Billable', color: '#2e7d32', bg: '#e8f5e9' },
  { value: 'non_billable', label: 'Non-Billable', color: '#e65100', bg: '#fff3e0' },
  { value: 'descanso', label: 'Descanso', color: '#0277bd', bg: '#e1f5fe' },
  { value: 'vacaciones', label: 'Vacaciones', color: '#6a1b9a', bg: '#f3e5f5' },
  { value: 'permisos', label: 'Permisos', color: '#4a148c', bg: '#ede7f6' },
];

const HORAS = [];
for (let h = 0; h < 24; h++) {
  for (let m = 0; m < 60; m += 15) {
    HORAS.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
}

function calcularHoras(inicio, fin) {
  if (!inicio || !fin) return 0;
  const [h1, m1] = inicio.split(':').map(Number);
  const [h2, m2] = fin.split(':').map(Number);
  const mins = (h2 * 60 + m2) - (h1 * 60 + m1);
  return Math.max(0, Math.round(mins / 60 * 100) / 100);
}

function formatHoras(horas) {
  if (!horas || horas === 0) return '0h';
  const h = Math.floor(horas);
  const m = Math.round((horas - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

function getWeekDates(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  const days = [];
  for (let i = 0; i < 7; i++) {
    const next = new Date(monday);
    next.setDate(monday.getDate() + i);
    days.push(next);
  }
  return days;
}

function formatDateLocal(date) {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateDisplay(date) {
  const d = new Date(date);
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}`;
}

function formatDateFull(date) {
  const d = new Date(date);
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
}

const DIAS = ['Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa', 'Do'];

const ESTADO_CONFIG = {
  pendiente: { label: 'Pendiente', color: 'default', icon: <AccessTimeIcon fontSize="small" /> },
  aprobado: { label: 'Aprobado', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  rechazado: { label: 'Rechazado', color: 'error', icon: <CancelIcon fontSize="small" /> },
};

export default function Timesheet() {
  const { usuario } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [registros, setRegistros] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [tareas, setTareas] = useState([]);
  const [tareaSeleccionada, setTareaSeleccionada] = useState('');
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [error, setError] = useState('');
  const [aprobacion, setAprobacion] = useState(null);
  const [openAprobar, setOpenAprobar] = useState(false);
  const [comentarioAdmin, setComentarioAdmin] = useState('');
  const [form, setForm] = useState({
    proyecto_id: '',
    fecha: formatDateLocal(new Date()),
    hora_inicio: '08:00',
    hora_fin: '09:00',
    tipo: 'billable',
    descripcion: ''
  });

  const weekDays = getWeekDates(currentDate);
  const fechaInicio = formatDateLocal(weekDays[0]);
  const fechaFin = formatDateLocal(weekDays[6]);
  const horasCalculadas = calcularHoras(form.hora_inicio, form.hora_fin);

  const tareasPorProyecto = form.proyecto_id
    ? tareas.filter(t => t.proyecto_id === parseInt(form.proyecto_id))
    : [];

  const cargarDatos = () => {
    axios.get(`${API}/api/tiempos/usuario/${usuario.id}?fecha_inicio=${fechaInicio}&fecha_fin=${fechaFin}`)
      .then(r => setRegistros(Array.isArray(r.data) ? r.data : []));
    axios.get(`${API}/api/proyectos`, { params: { empresa_id: usuario?.empresa_id } })
      .then(r => setProyectos(Array.isArray(r.data) ? r.data : []));
    axios.get(`${API}/api/tareas`, { params: { empresa_id: usuario?.empresa_id } })
      .then(r => setTareas(Array.isArray(r.data) ? r.data : []));
    axios.get(`${API}/api/tiempos/aprobacion/${usuario.id}?fecha_inicio=${fechaInicio}`)
      .then(r => setAprobacion(r.data))
      .catch(() => setAprobacion({ estado: 'pendiente' }));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargarDatos(); }, [currentDate]);

  const semanaAnterior = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() - 7);
    setCurrentDate(d);
  };

  const semanaSiguiente = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + 7);
    setCurrentDate(d);
  };

  const abrirCrear = () => {
    setEditando(null);
    setTareaSeleccionada('');
    setForm({
      proyecto_id: '',
      fecha: fechaInicio,
      hora_inicio: '08:00',
      hora_fin: '09:00',
      tipo: 'billable',
      descripcion: ''
    });
    setError('');
    setOpen(true);
  };

  const abrirEditar = (registro) => {
    setEditando(registro);
    setTareaSeleccionada('');
    setForm({
      proyecto_id: registro.proyecto_id || '',
      fecha: formatDateLocal(new Date(registro.fecha)),
      hora_inicio: registro.hora_inicio || '08:00',
      hora_fin: registro.hora_fin || '09:00',
      tipo: registro.tipo,
      descripcion: registro.descripcion || ''
    });
    setError('');
    setOpen(true);
  };

  const guardarRegistro = async () => {
    const horas = calcularHoras(form.hora_inicio, form.hora_fin);
    if (horas <= 0) {
      setError('La hora de fin debe ser mayor que la hora de inicio');
      return;
    }
    try {
      const datos = {
        usuario_id: usuario.id,
        proyecto_id: form.proyecto_id || null,
        empresa_id: usuario?.empresa_id || null,
        fecha: form.fecha,
        horas: horas,
        hora_inicio: form.hora_inicio,
        hora_fin: form.hora_fin,
        tipo: form.tipo,
        descripcion: form.descripcion
      };
      if (editando) {
        await axios.put(`${API}/api/tiempos/${editando.id}`, datos);
      } else {
        await axios.post(`${API}/api/tiempos`, datos);
      }
      setOpen(false);
      setError('');
      cargarDatos();
    } catch (err) {
      setError('Error al guardar el registro');
    }
  };

  const eliminarRegistro = async (id) => {
    await axios.delete(`${API}/api/tiempos/${id}`);
    cargarDatos();
  };

  const aprobarSemana = async (estado) => {
    await axios.post(`${API}/api/tiempos/aprobacion`, {
      usuario_id: usuario.id,
      empresa_id: usuario?.empresa_id,
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      estado,
      aprobado_por: usuario.id,
      comentario: comentarioAdmin
    });
    setOpenAprobar(false);
    setComentarioAdmin('');
    cargarDatos();
  };

  const exportarCSV = () => {
    const headers = ['Fecha', 'Proyecto', 'Descripción', 'Tipo', 'Inicio', 'Fin', 'Horas'];
    const rows = registros.map(r => [
      r.fecha?.split('T')[0],
      proyectos.find(p => p.id === r.proyecto_id)?.nombre || '—',
      r.descripcion || '—',
      r.tipo,
      r.hora_inicio || '—',
      r.hora_fin || '—',
      r.horas
    ]);
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `timesheet_${fechaInicio}_${fechaFin}.csv`;
    a.click();
  };

  const filas = registros.map(r => ({
    registro: r,
    proyecto_id: r.proyecto_id,
    tipo: r.tipo,
    horasPorDia: weekDays.map(day => {
      const dayStr = formatDateLocal(day);
      const registroFecha = r.fecha ? formatDateLocal(new Date(r.fecha)) : '';
      return registroFecha === dayStr ? [r] : [];
    }),
    totalHoras: r.horas
  }));

  const totalesDia = weekDays.map(day => {
    const dayStr = formatDateLocal(day);
    return registros.filter(r => {
      const registroFecha = r.fecha ? formatDateLocal(new Date(r.fecha)) : '';
      return registroFecha === dayStr;
    }).reduce((sum, r) => sum + r.horas, 0);
  });

  const totalGeneral = totalesDia.reduce((a, b) => a + b, 0);
  const estadoAprobacion = aprobacion?.estado || 'pendiente';
  const configEstado = ESTADO_CONFIG[estadoAprobacion];

  return (
    <Container sx={{ mt: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Timesheet</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportarCSV}>
            Exportar CSV
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirCrear}>
            Registrar Horas
          </Button>
        </Box>
      </Box>

      {/* Navegación semana + estado aprobación */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={semanaAnterior}><NavigateBeforeIcon /></IconButton>
          <Typography variant="h6">
            {formatDateFull(weekDays[0])} — {formatDateFull(weekDays[6])}
          </Typography>
          <IconButton onClick={semanaSiguiente}><NavigateNextIcon /></IconButton>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            icon={configEstado.icon}
            label={configEstado.label}
            color={configEstado.color}
            variant="outlined"
          />
          {(usuario?.rol === 'admin' || usuario?.rol === 'super_admin') && (
            <Button size="small" variant="outlined" onClick={() => setOpenAprobar(true)}>
              Gestionar
            </Button>
          )}
        </Box>
      </Box>

      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#1F4E79' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold', minWidth: 220 }}>
                Proyecto / Descripción
              </TableCell>
              {weekDays.map((day, i) => (
                <TableCell key={i} align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                  <Box>{DIAS[i]}</Box>
                  <Box sx={{ fontSize: 10 }}>{formatDateDisplay(day)}</Box>
                </TableCell>
              ))}
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Total</TableCell>
              <TableCell sx={{ color: 'white' }}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filas.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No hay registros esta semana
                </TableCell>
              </TableRow>
            )}
            {filas.map((fila, idx) => {
              const tipoConfig = TIPOS.find(t => t.value === fila.tipo);
              const proyecto = proyectos.find(p => p.id === fila.proyecto_id);
              const r = fila.registro;
              return (
                <TableRow key={r.id} sx={{ backgroundColor: idx % 2 === 0 ? '#F8F9FA' : 'white' }}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                      <Box sx={{
                        width: 10, height: 10, borderRadius: '50%',
                        backgroundColor: tipoConfig?.color,
                        mt: 0.7, flexShrink: 0
                      }} />
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {proyecto?.nombre || '— Sin proyecto'}
                        </Typography>
                        {r.descripcion && (
                          <Typography variant="caption" color="text.secondary">
                            {r.descripcion}
                          </Typography>
                        )}
                        {r.hora_inicio && r.hora_fin && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {r.hora_inicio} – {r.hora_fin}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  {fila.horasPorDia.map((regs, i) => (
                    <TableCell key={i} align="center">
                      {regs.length > 0 ? (
                        <Typography variant="body2" fontWeight="bold" color={tipoConfig?.color}>
                          {formatHoras(r.horas)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    <Typography fontWeight="bold" color="primary">{formatHoras(r.horas)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton size="small" onClick={() => abrirEditar(r)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => eliminarRegistro(r.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
            <TableRow sx={{ backgroundColor: '#1F4E79' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Total</TableCell>
              {totalesDia.map((h, i) => (
                <TableCell key={i} align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                  {h > 0 ? formatHoras(h) : '—'}
                </TableCell>
              ))}
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                {formatHoras(totalGeneral)}
              </TableCell>
              <TableCell />
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Resumen por tipo */}
      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        {TIPOS.map(tipo => {
          const total = registros.filter(r => r.tipo === tipo.value).reduce((sum, r) => sum + r.horas, 0);
          return (
            <Paper key={tipo.value} sx={{ p: 2, flex: 1, textAlign: 'center', borderTop: `3px solid ${tipo.color}` }}>
              <Typography variant="caption" sx={{ color: tipo.color, fontWeight: 'bold', textTransform: 'uppercase' }}>
                {tipo.label}
              </Typography>
              <Typography variant="h5" fontWeight="bold">{formatHoras(total)}</Typography>
            </Paper>
          );
        })}
      </Box>

      {/* Dialog registrar/editar horas */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth>
        <DialogTitle>{editando ? 'Editar Registro' : 'Registrar Horas'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{error}</Alert>}
          <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
            <InputLabel>Tipo</InputLabel>
            <Select value={form.tipo} label="Tipo"
              onChange={e => setForm({ ...form, tipo: e.target.value, proyecto_id: '', descripcion: '' })}>
              {TIPOS.map(t => (
                <MenuItem key={t.value} value={t.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: t.color }} />
                    {t.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {form.tipo === 'billable' && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Proyecto</InputLabel>
              <Select
                value={form.proyecto_id}
                label="Proyecto"
                onChange={e => {
                  setForm({ ...form, proyecto_id: e.target.value, descripcion: '' });
                  setTareaSeleccionada('');
                }}
              >
                <MenuItem value="">— Sin proyecto —</MenuItem>
                {proyectos.map(p => <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>)}
              </Select>
            </FormControl>
          )}

          {form.tipo === 'billable' && form.proyecto_id && tareasPorProyecto.length > 0 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Tarea (opcional)</InputLabel>
              <Select
                value={tareaSeleccionada}
                label="Tarea (opcional)"
                onChange={e => {
                  const tarea = tareas.find(t => t.id === e.target.value);
                  setTareaSeleccionada(e.target.value);
                  if (tarea) {
                    setForm({ ...form, descripcion: tarea.titulo });
                  } else {
                    setForm({ ...form, descripcion: '' });
                  }
                }}
              >
                <MenuItem value="">— Otra actividad —</MenuItem>
                {tareasPorProyecto.map(t => (
                  <MenuItem key={t.id} value={t.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={t.estado}
                        size="small"
                        color={t.estado === 'completada' ? 'success' : t.estado === 'en_progreso' ? 'info' : 'default'}
                      />
                      {t.titulo}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            fullWidth label="Fecha" type="date" value={form.fecha}
            onChange={e => setForm({ ...form, fecha: e.target.value })}
            sx={{ mb: 2 }} InputLabelProps={{ shrink: true }}
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Hora inicio</InputLabel>
              <Select value={form.hora_inicio} label="Hora inicio"
                onChange={e => setForm({ ...form, hora_inicio: e.target.value })}>
                {HORAS.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Hora fin</InputLabel>
              <Select value={form.hora_fin} label="Hora fin"
                onChange={e => setForm({ ...form, hora_fin: e.target.value })}>
                {HORAS.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
          {horasCalculadas > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Duración calculada: <strong>{formatHoras(horasCalculadas)}</strong>
            </Alert>
          )}
          <TextField
            fullWidth label="Descripción" value={form.descripcion}
            onChange={e => setForm({ ...form, descripcion: e.target.value })}
            multiline rows={2}
            helperText="Puedes seleccionar una tarea arriba o escribir una descripción libre"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={guardarRegistro}>
            {editando ? 'Guardar' : 'Registrar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog aprobar semana */}
      <Dialog open={openAprobar} onClose={() => setOpenAprobar(false)} fullWidth>
        <DialogTitle>Gestionar Aprobación de Semana</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Semana: {formatDateFull(weekDays[0])} — {formatDateFull(weekDays[6])}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Total horas registradas: <strong>{formatHoras(totalGeneral)}</strong>
          </Typography>
          <TextField
            fullWidth label="Comentario (opcional)" value={comentarioAdmin}
            onChange={e => setComentarioAdmin(e.target.value)}
            multiline rows={3} sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAprobar(false)}>Cancelar</Button>
          <Button variant="outlined" color="error" startIcon={<CancelIcon />}
            onClick={() => aprobarSemana('rechazado')}>
            Rechazar
          </Button>
          <Button variant="contained" color="success" startIcon={<CheckCircleIcon />}
            onClick={() => aprobarSemana('aprobado')}>
            Aprobar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
